import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Film, X } from "lucide-react";
import { API } from "@/lib/api";
import { LOGO_URL } from "@/lib/assets";

const TABS = [
  {
    key: "Historical",
    blurb: "Archive shots from decades of Darlington clay — the cars, characters and track days that built this place since 1968.",
  },
  {
    key: "Recent Events",
    blurb: "Fresh from the fence — the latest race nights, title events and club days from the current era.",
  },
];

export default function Gallery() {
  const [tab, setTab] = useState("Historical");
  const [folders, setFolders] = useState(["Historical", "Recent Events"]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(`${API}/gallery/folders`)
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d) && d.length) setFolders(d);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/gallery/items?gallery=${encodeURIComponent(tab)}`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab]);

  const blurb = TABS.find((t) => t.key === tab)?.blurb || `Club collection — ${tab}.`;

  return (
    <main className="min-h-screen bg-clay-bg">
      <header className="border-b border-clay-border bg-clay-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <a data-testid="gallery-logo" href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Mid Western Speedway logo" className="w-10 h-10 object-cover rounded-sm ring-1 ring-clay-border" />
            <span className="font-display font-bold uppercase text-lg tracking-wide">
              MWS <span className="text-clay-orange">Gallery</span>
            </span>
          </a>
          <a
            data-testid="gallery-back-link"
            href="/"
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14 lg:py-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4"
        >
          Dust, Thunder & Memories
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-none mb-10"
        >
          The Gallery
        </motion.h1>

        <div className="flex flex-wrap gap-2 mb-4">
          {folders.map((t) => (
            <button
              key={t}
              data-testid={`gallery-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setTab(t)}
              className={`font-display font-bold uppercase tracking-wider px-6 py-3 border transition-all duration-300 ${
                tab === t
                  ? "bg-clay-orange text-clay-bg border-clay-orange clay-glow"
                  : "border-clay-border text-clay-muted hover:border-clay-orange/60 hover:text-clay-text"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <p className="text-clay-muted max-w-2xl mb-10">{blurb}</p>

        {!loading && items.some((i) => i.is_cover) && (
          <motion.figure
            data-testid="gallery-cover"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="relative border border-clay-border overflow-hidden mb-8 cursor-pointer group"
            onClick={() => setSelected(items.find((i) => i.is_cover))}
          >
            <img
              src={`${API.replace("/api", "")}${items.find((i) => i.is_cover).url}`}
              alt={items.find((i) => i.is_cover).caption || `${tab} cover photo`}
              className="w-full h-[45vh] sm:h-[60vh] object-cover group-hover:scale-[1.02] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-clay-bg/90 via-transparent to-transparent pointer-events-none" />
            <figcaption className="absolute bottom-0 left-0 p-6 sm:p-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-gold border border-clay-gold/40 px-2 py-1 bg-clay-bg/60 backdrop-blur-sm">
                Featured — {tab}
              </span>
              {items.find((i) => i.is_cover).caption && (
                <p className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight mt-3">
                  {items.find((i) => i.is_cover).caption}
                </p>
              )}
            </figcaption>
          </motion.figure>
        )}

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted py-20 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <div data-testid="gallery-empty" className="border border-dashed border-clay-border py-24 text-center">
            <Film className="w-10 h-10 text-clay-muted mx-auto mb-4" />
            <p className="font-display font-bold uppercase text-xl tracking-wide mb-2">Nothing here yet</p>
            <p className="text-clay-muted text-sm">Check back after the next race night — the club uploads fresh shots regularly.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.filter((i) => !i.is_cover).map((item, i) => (
              <motion.button
                key={item.id}
                data-testid="gallery-item"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                onClick={() => setSelected(item)}
                className="group relative border border-clay-border bg-clay-card overflow-hidden text-left hover:border-clay-orange/60 transition-colors duration-300"
              >
                {item.content_type?.startsWith("video/") ? (
                  <div className="relative aspect-[4/3] bg-black grid place-items-center">
                    <video src={`${API.replace("/api", "")}${item.url}`} className="w-full h-full object-cover" muted preload="metadata" />
                    <span className="absolute inset-0 grid place-items-center bg-black/30">
                      <Film className="w-10 h-10 text-clay-gold" />
                    </span>
                  </div>
                ) : (
                  <img
                    src={`${API.replace("/api", "")}${item.url}`}
                    alt={item.caption || "Gallery photo"}
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                )}
                {item.caption && (
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-clay-muted px-4 py-3 border-t border-clay-border/60">
                    {item.caption}
                  </p>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setSelected(null)}
              className="absolute top-5 right-5 text-clay-muted hover:text-clay-text transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full"
            >
              {selected.content_type?.startsWith("video/") ? (
                <video
                  src={`${API.replace("/api", "")}${selected.url}`}
                  controls
                  autoPlay
                  className="w-full max-h-[75vh] bg-black border border-clay-border"
                />
              ) : (
                <img
                  src={`${API.replace("/api", "")}${selected.url}`}
                  alt={selected.caption || "Gallery photo"}
                  className="w-full max-h-[75vh] object-contain border border-clay-border bg-clay-card"
                />
              )}
              {selected.caption && (
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay-muted text-center mt-4">{selected.caption}</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
